<template>
  <Form class="card" @submit="createKssTask">
    <div v-if="step === 1" class="flex flex-col md:flex-row">
      <div class="flex flex-col justify-evenly w-full md:w-1/2 md:mr-8 mb-8">
        <div class="w-full flex flex-col justify-between mt-4">
          <div class="flex items-center m-8">
            <font-awesome-icon icon="fa-calendar-day" class="mr-4 text-3xl"/>
            <p class="text-xl">{{ $t('KssCreatorComponent.selectDate') }}</p>
          </div>
          <kss-calendar-component/>
        </div>
      </div>
      <div class="flex flex-col justify-start w-full md:w-1/2 md:ml-8">
        <div class="w-full flex flex-col justify-between -mt-2">
          <div class="flex items-center m-8">
            <font-awesome-icon icon="fa-clock" class="mr-4 text-3xl"/>
                <p class="text-xl">{{ $t('KssCreatorComponent.selectTime') }}</p>
          </div>
          <div class="mb-4 rounded-lg bg-white p-8 border border-[#e2e8f0]" id="div_runtime">
            <kss-runtime-component/>
          </div>
        </div>
      </div>
    </div>
    <div v-if="step === 2">
      <div class="flex items-center m-8">
        <font-awesome-icon :icon="['fas', 'users-viewfinder']" class="mr-3 text-3xl"/>
        <p class="text-xl">{{ $t('KssCreatorComponent.furtherInfo') }}</p>
      </div>
      <div class="text-[var(--color-orange)] flex flex-row justify-end items-center mr-5">
          <label class="flex items-center text-xl mr-8 text-center" id="lbl_calDate">
            <font-awesome-icon icon="fa-calendar" class="text-xl md:text-2xl mr-3"/>
            {{ getFormattedDate ?? $t('KssRuntimeComponent.selectDate') }}
          </label>
          <label class="flex items-center text-xl mr-8 text-center" id="lbl_cal-time">
            <font-awesome-icon icon="fa-clock" class="text-xl md:text-2xl mr-3"/>
            {{ getFormattedTime ?? $t('KssRuntimeComponent.selectRuntime') }}
          </label>
        </div>
        <kss-form-component/>
    </div>
    <div class="flex sm:my-8 sm:w-fit sm:float-right flex-col sm:flex-row w-full pt-8 items-center
        gap-3">
      <Button v-if="step === 1" @click="step = 2"
        class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled>
        {{ $t('KssCreatorComponent.nextButton') }}
      </Button>
      <Button v-if="step === 2" @click="step = 1"
        class="btn-secondary w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled>
        {{ $t('KssCreatorComponent.backButton') }}
      </Button>
      <Button v-if="step === 2" @click.prevent="createKssTask"
        class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled>
        {{ $t('KssCreatorComponent.createKssTracker') }}
      </Button>
    </div>
  </Form>
  <SimilarKssWarningOverlayComponent
        v-model:visible="showDialog" @confirmed="onConfirmed"
        :data="dialogData"></SimilarKssWarningOverlayComponent>
  <Dialog
    v-model:visible="roleAccessDialogVisible"
    modal
    header="Some participants are not eligible"
    :style="{ width: '40rem' }"
  >
    <p class="mb-3">
      This assessment can only be assigned to users with one of these roles:
      <strong>{{ roleAccessError.requiredRoles.join(', ') }}</strong>
    </p>
    <p class="mb-2">Please remove the following users and try again:</p>
    <ul class="list-disc pl-4">
      <li v-for="user in roleAccessError.ineligibleUsers" :key="user.userId">
        {{ user.name || user.email || user.userId }}
        <span v-if="user.roles && user.roles.length"> (current roles: {{ user.roles.join(', ') }})</span>
      </li>
    </ul>
  </Dialog>
</template>

<script>
import KssFormComponent from '@/components/Home/KssCreator/KssFormComponent.vue';
import KssRuntimeComponent from '@/components/Home/KssCreator/KssRuntimeComponent.vue';
import KssCalendarComponent from '@/components/Home/KssCreator/KssCalendarComponent.vue';
import { Form } from '@primevue/forms';
import { Button } from 'primevue';
import Dialog from 'primevue/dialog';
import SimilarKssWarningOverlayComponent from '../Shared/FrontendOverlays/SimilarKssWarningOverlayComponent.vue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'KssCreatorComponent',
  props: {
    data: Object,
  },
  components: {
    KssFormComponent,
    KssRuntimeComponent,
    KssCalendarComponent,
    Form,
    Button,
    Dialog,
    SimilarKssWarningOverlayComponent,
  },
  data() {
    return {
      step: 1,
      showDialog: false,
      dialogData: {},
      roleAccessDialogVisible: false,
      roleAccessError: {
        requiredRoles: [],
        ineligibleUsers: [],
      },
    };
  },
  watch: {
    checked() {
      this.$emit('input', this.displayArchivedTasks);
    },
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'kssTracker',
        this.$t('FallbackTexts.collaborationTracker'),
        this.$i18n?.locale,
      );
    },
    createdTasks() {
      return this.$store.getters.getCurrentCreatedTasks;
    },
    allKssParticipants() {
      return this.$store.getters.getKssParticipants;
    },
    involvedKssUsers() {
      return this.$store.getters.getInvolvedKssUsers;
    },
    archivedTasks() {
      return this.$store.getters.getCurrentArchivedTasks;
    },
    getFormattedDate() {
      const date = this.$store.getters.getSelectedDate;
      if (!date) {
        return null;
      }
      return `${date.getDate()}.${(date.getMonth() + 1)}.${date.getFullYear()}`;
    },
    getFormattedTime() {
      if (!this.$store.state.kss.selectedHours && this.$store.state.kss.selectedHours !== 0) {
        return null;
      }
      return `${(`0${this.$store.state.kss.selectedHours}`).slice(-2)}:${(`0${this.$store.state.kss.selectedMinutes}`).slice(-2)}`;
    },
    showCalendar: {
      get() {
        return true;
        // return this.$store.state.kss.showCalendar;
      },
      set(value) {
        this.$store.commit('setShowCalendar', value);
      },
    },
    showRuntime: {
      get() {
        return this.$store.state.kss.showRuntime;
      },
      set(value) {
        this.$store.commit('setShowRuntime', value);
      },
    },
    filterCreatedTasks() {
      return this.createdTasks.filter((el) => el.name.toLowerCase()
        .includes(this.searchbar.toLowerCase())
      || el.description.toLowerCase().includes(this.searchbar.toLowerCase())).toSorted();
    },
    filterArchivedTasks() {
      return this.archivedTasks.filter((el) => el.name.toLowerCase()
        .includes(this.searchbar.toLowerCase())
      || el.description.toLowerCase().includes(this.searchbar.toLowerCase())).toSorted();
    },
  },
  methods: {
    async closePopup() {
      // Close the parent TaskCreatorOverlayComponent
      await this.$nextTick();
      this.$store.commit('removeFrontendOverlay', this.data.id);
      this.$emit('close-overlay');
    },
    async createKssTask() {
      try {
        if (this.checkForErrors()) {
          return;
        }

        const data = {
          involvedUsers: this.$store.state.kss.kssParticipants.filter((e) => e.isChecked)
            .map((e) => e._id),
          name: this.$store.state.kss.newMeetingSubject,
          description: this.$store.state.kss.newDescription,
        };

        if (this.$store.state.kss.projectId) {
          data.projectId = this.$store.state.kss.projectId;
        }

        const dateTaskBegin = new Date(
          this.$store.state.kss.selectedDate.getFullYear(),
          this.$store.state.kss.selectedDate.getMonth(),
          this.$store.state.kss.selectedDate.getDate(),
          this.$store.state.kss.selectedHours,
          this.$store.state.kss.selectedMinutes,
        );

        if (this.$store.state.kss.isRecurringTask) {
          data.repeatRules = this.getRecurringObject(dateTaskBegin.toUTCString());
        } else {
          data.date_taskBegin = dateTaskBegin.toUTCString();
          data.repeatRules = [];
        }

        data.involvedExternalUsers = this.$store.state.kss.inviteEmails;

        await this.$store.dispatch('createKssTask', data);
        if (!this.$store.state.kss.hasSimilarTasks) {
          this.$store.commit('setShowCalendar', true);
          this.$store.commit('setShowRuntime', true);
          this.$toast.add({
            severity: 'success',
            summary: this.$t('ToastComponent.kss.kssCreated', { text: this.featureName }),
            detail: '',
            life: 3000,
          });
          this.closePopup();
        } else {
          this.dialogData = { ...data };
          /* this.$store.commit('addFrontendOverlay', {
            id: Math.random(),
            type: 'similarKssTasks',
            postData: data,
          }); */
          this.showDialog = true;
        }
      } catch (error) {
        const roleDeniedHandled = await this.$store.dispatch(
          'assessmentRoleDenied/maybeShowFromApiError',
          error,
          { root: true },
        );
        if (roleDeniedHandled) {
          return;
        }
        const payload = this.getRoleAccessErrorPayload(error);
        if (payload) {
          this.roleAccessError = payload;
          this.roleAccessDialogVisible = true;
          return;
        }
        this.$toast.add({
          severity: 'error',
          summary: error.response?.data?.message,
          detail: '',
          life: 3000,
        });
      }
    },
    getRoleAccessErrorPayload(error) {
      const data = error?.response?.data || {};
      if (data?.code === 'SCHEMA_ROLE_ACCESS_DENIED') {
        return {
          requiredRoles: data.requiredRoles || [],
          ineligibleUsers: data.ineligibleUsers || [],
        };
      }
      const nested = data?.message;
      if (nested?.code === 'SCHEMA_ROLE_ACCESS_DENIED') {
        return {
          requiredRoles: nested.requiredRoles || [],
          ineligibleUsers: nested.ineligibleUsers || [],
        };
      }
      return null;
    },
    async onConfirmed() {
      this.showDialog = false;
      this.closePopup();
    },
    checkForErrors() {
      let hasError = false;
      if (!this.$store.state.kss.selectedDate) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.kss.selectDateErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (!this.$store.state.kss.selectedHours && this.$store.state.kss.selectedHours !== 0) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.kss.selectTimeErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if ((this.$store.state.kss.selectedHours || this.$store.state.kss.selectedHours === 0)
        && this.$store.state.kss.selectedDate
        && !this.checkDateAndTimeValid()
        && !this.$store.state.kss.isRecurringTask) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.kss.pastDateErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (!this.$store.state.kss.newMeetingSubject) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.kss.selectSubjectErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (this.$store.state.kss.kssParticipants.filter((e) => e.isChecked).length === 0
        && this.$store.state.kss.inviteEmails.length === 0) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.kss.selectParticipantsErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (this.$store.state.kss.isRecurringTask) {
        const type = this.$store.state.kss.recurringType;
        const subType = this.$store.state.kss.recurringSubtype;
        if (type === 'weekly' || type === 'monthly' || (type === 'custom' && subType === 'weeks')) {
          const len = this.$store.state.kss.recurringDays
            .filter((e) => e.isSelected).length;

          if (len === 0) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('ToastComponent.kss.selectRecurringDays'),
              detail: '',
              life: 3000,
            });
            hasError = true;
          }
        }
      }

      return hasError;
    },
    checkDateAndTimeValid() {
      const selectedDate = new Date(this.$store.state.kss.selectedDate);
      const { selectedHours } = this.$store.state.kss;
      const { selectedMinutes } = this.$store.state.kss;
      selectedDate.setHours(selectedHours, selectedMinutes);

      const currentTime = new Date();
      const timeDifference = currentTime - selectedDate;
      return timeDifference <= 60 * 60 * 1000;
    },
    appendSeriesEndDate(rule) {
      const endDate = this.$store.state.kss.seriesEndDate;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(
          this.$store.state.kss.selectedHours ?? 0,
          this.$store.state.kss.selectedMinutes ?? 0,
          0,
          0,
        );
        rule.end = end.toUTCString();
      }
      return rule;
    },
    getRecurringObject(dateTaskBegin) {
      switch (this.$store.state.kss.recurringType) {
        case 'daily': {
          const rule = {
            frequency: 'DAILY',
            start: dateTaskBegin,
          };
          if (!this.$store.state.kss.includeWeekends) {
            rule.byDayOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR'];
          }
          return [this.appendSeriesEndDate(rule)];
        }
        case 'weekly':
          return [this.appendSeriesEndDate({
            frequency: 'WEEKLY',
            byDayOfWeek: this.$store.state.kss.recurringDays
              .filter((e) => e.isSelected)
              .map((e) => e.rSchName),
            start: dateTaskBegin,
          })];
        case 'monthly': {
          const selectedDays = this.$store.state.kss.recurringDays
            .filter((e) => e.isSelected)
            .map((e) => e.rSchName);
          return [this.appendSeriesEndDate({
            frequency: 'MONTHLY',
            start: dateTaskBegin,
            byDayOfWeek: selectedDays.length ? [selectedDays[0]] : [],
            byWeekOfMonth: this.$store.state.kss.recurringWeekOfMonth,
          })];
        }
        case 'custom':
          if (this.$store.state.kss.recurringSubtype === 'days') {
            const dailyRule = {
              frequency: 'DAILY',
              interval: this.$store.state.kss.recurringEveryX,
              start: dateTaskBegin,
            };
            if (!this.$store.state.kss.includeWeekends) {
              dailyRule.byDayOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR'];
            }
            return [this.appendSeriesEndDate(dailyRule)];
          }
          if (this.$store.state.kss.recurringSubtype === 'weeks') {
            return [this.appendSeriesEndDate({
              frequency: 'WEEKLY',
              interval: this.$store.state.kss.recurringEveryX,
              byDayOfWeek: this.$store.state.kss.recurringDays
                .filter((e) => e.isSelected)
                .map((e) => e.rSchName),
              start: dateTaskBegin,
            })];
          }
        // eslint-disable-next-line no-fallthrough
        default:
          return [];
      }
    },
    onToggleArchivedTasks() {
      this.displayArchivedTasks = !this.displayArchivedTasks;
      this.$emit('input', this.displayArchivedTasks);
    },
  },
  mounted() {
    this.$store.commit('setSelectedDate', new Date());
    this.$store.dispatch('getTeams');
    // pass the logged in user to select him by default
    this.$store.dispatch('getKssParticipants', this.$store.getters.getUser?.name);
  },
  unmounted() {
    this.$store.commit('removeTeams');
    this.$store.commit('removeKssTeams');
    this.$store.commit('resetKssFormValues');
    this.$store.commit('resetKssRecurringTaskValues');
    this.$store.commit('removeKssParticipants');
    this.$store.commit('resetInviteEmail');
  },
};
</script>
<style scoped>
.card {
    border: none !important;
    background-color: transparent;
}
</style>
